import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Slice,
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  TreeDeciduous,
  Home,
  Gift,
  Utensils,
  Award,
  Ruler,
  Palette
} from 'lucide-react';
import { toast } from 'sonner';

// Wood types
const WOOD_TYPES = [
  { value: 'ulivo', label: 'Legno di ulivo', icon: TreeDeciduous },
  { value: 'noce', label: 'Legno di noce', icon: TreeDeciduous },
  { value: 'acacia', label: 'Legno di acacia', icon: TreeDeciduous },
  { value: 'faggio', label: 'Legno di faggio', icon: TreeDeciduous },
  { value: 'ciliegio', label: 'Legno di ciliegio', icon: TreeDeciduous },
  { value: 'rovere', label: 'Legno di rovere', icon: TreeDeciduous },
  { value: 'teak', label: 'Legno di teak', icon: TreeDeciduous },
  { value: 'bamboo', label: 'Bambù', icon: TreeDeciduous },
  { value: 'misto', label: 'Legni misti / Intarsio', icon: Sparkles },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Board types
const BOARD_TYPES = [
  { value: 'classico', label: 'Tagliere classico rettangolare', icon: Slice },
  { value: 'rotondo', label: 'Tagliere rotondo', icon: Slice },
  { value: 'forma', label: 'Tagliere sagomato (forma speciale)', icon: Sparkles },
  { value: 'manico', label: 'Tagliere con manico', icon: Slice },
  { value: 'vassoio', label: 'Tagliere da portata / Vassoio', icon: Utensils },
  { value: 'set', label: 'Set di taglieri', icon: Slice },
  { value: 'pane', label: 'Tagliere per pane con raccogli briciole', icon: Slice },
  { value: 'formaggi', label: 'Tagliere per formaggi e salumi', icon: Utensils },
  { value: 'altro', label: 'Altro', icon: Slice },
];

// Size options
const SIZE_OPTIONS = [
  { value: 'piccolo', label: 'Piccolo (25-30 cm)', icon: Ruler },
  { value: 'medio', label: 'Medio (35-45 cm)', icon: Ruler },
  { value: 'grande', label: 'Grande (50-60 cm)', icon: Ruler },
  { value: 'xl', label: 'Extra Large (70+ cm)', icon: Ruler },
  { value: 'personalizzato', label: 'Misure personalizzate', icon: Sparkles },
];

// Finish options
const FINISH_OPTIONS = [
  { value: 'naturale', label: 'Finitura naturale (olio)', icon: TreeDeciduous },
  { value: 'cerato', label: 'Cerato (cera d\'api)', icon: Star },
  { value: 'rustico', label: 'Stile rustico / Live edge', icon: TreeDeciduous },
  { value: 'levigato', label: 'Levigato liscio', icon: Sparkles },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Personalization options
const PERSONALIZATIONS = [
  { value: 'incisione_nome', label: 'Incisione nome/iniziali', icon: Palette },
  { value: 'incisione_dedica', label: 'Incisione dedica/frase', icon: Heart },
  { value: 'incisione_logo', label: 'Incisione logo', icon: Award },
  { value: 'incisione_disegno', label: 'Incisione disegno personalizzato', icon: Sparkles },
  { value: 'nessuna', label: 'Nessuna personalizzazione', icon: Slice },
];

// Purpose/Occasion
const PURPOSE_OPTIONS = [
  { value: 'cucina', label: 'Per uso quotidiano in cucina', icon: Utensils },
  { value: 'portata', label: 'Per servire in tavola', icon: Utensils },
  { value: 'regalo', label: 'Come regalo', icon: Gift },
  { value: 'matrimonio', label: 'Regalo matrimonio / Lista nozze', icon: Heart },
  { value: 'inaugurazione', label: 'Regalo inaugurazione casa', icon: Home },
  { value: 'ristorante', label: 'Per ristorante / Attività', icon: Utensils },
  { value: 'collezione', label: 'Per collezione / Arredo', icon: Star },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '30-50', label: '€30 - €50', description: 'Tagliere semplice' },
  { value: '50-80', label: '€50 - €80', description: 'Legno pregiato' },
  { value: '80-120', label: '€80 - €120', description: 'Con personalizzazione' },
  { value: '120-180', label: '€120 - €180', description: 'Grande / Intarsiato' },
  { value: '180-300', label: '€180 - €300', description: 'Pezzo da esposizione' },
  { value: '300+', label: '€300+', description: 'Opera d\'arte / Set completo' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '1week', label: '1 Settimana', description: 'Urgente' },
  { value: '2weeks', label: '2 Settimane', description: 'Prioritario' },
  { value: '3weeks', label: '3 Settimane', description: 'Standard' },
  { value: '1month', label: '1 Mese', description: 'Con calma' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example boards
const BOARD_EXAMPLES = [
  {
    idea: "Tagliere in ulivo con incisione per matrimonio",
    result: "Tagliere rettangolare 45cm in ulivo pugliese con incisione nomi sposi e data, bordo naturale live edge",
    image: "https://images.unsplash.com/photo-1617695615794-a5abcece0f48?w=400&h=300&fit=crop",
    category: "Matrimonio"
  },
  {
    idea: "Tagliere da portata per formaggi e salumi",
    result: "Tagliere rotondo 40cm in noce nazionale con scanalatura perimetrale per raccolta liquidi",
    image: "https://images.unsplash.com/photo-1586765677067-f8030bd8e303?w=400&h=300&fit=crop",
    category: "Portata"
  },
  {
    idea: "Set taglieri per cucina professionale",
    result: "Set 3 taglieri in acacia: piccolo per erbe, medio per verdure, grande per carni, con supporto verticale",
    image: "https://images.unsplash.com/photo-1607301405390-d831c242f59b?w=400&h=300&fit=crop",
    category: "Professionale"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho ordinato un tagliere in ulivo per il matrimonio di mia sorella. L'incisione con i loro nomi è venuta perfetta e il legno ha delle venature stupende. Un regalo che useranno per sempre!",
    author: "Francesca L.",
    location: "Bari"
  },
  {
    text: "Gestisco un ristorante e cercavo taglieri da portata unici. Il falegname ha capito esattamente le mie esigenze e ora i clienti fotografano i nostri piatti serviti su questi taglieri!",
    author: "Marco T.",
    location: "Bologna"
  },
  {
    text: "Volevo un tagliere grande per la mia nuova cucina. Quello in noce che mi hanno fatto è una vera opera d'arte, lo uso tutti i giorni ed è sempre più bello.",
    author: "Anna R.",
    location: "Torino"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Quale legno è migliore per un tagliere?",
    answer: "L'ulivo è il più pregiato: naturalmente antibatterico, durissimo e con venature uniche. Il noce è elegante e resistente. L'acacia è ottima per uso intensivo. Per taglieri da portata, il ciliegio ha tonalità calde bellissime."
  },
  {
    question: "Come si mantiene un tagliere in legno?",
    answer: "Lavalo a mano con acqua tiepida e sapone neutro, asciugalo subito. Ogni 2-3 mesi trattalo con olio alimentare (lino o minerale). Mai in lavastoviglie! Con queste cure durerà generazioni."
  },
  {
    question: "Si può incidere qualsiasi disegno?",
    answer: "Sì, l'incisione laser permette di riprodurre nomi, loghi, disegni e anche fotografie stilizzate. L'artigiano ti invierà un'anteprima prima di procedere."
  },
  {
    question: "Quanto tempo ci vuole per un tagliere personalizzato?",
    answer: "Un tagliere semplice richiede 1-2 settimane. Con incisioni personalizzate calcola 2-3 settimane. Per forme speciali o intarsi, 3-4 settimane."
  },
  {
    question: "Posso scegliere lo spessore del tagliere?",
    answer: "Assolutamente! Taglieri sottili (2-3cm) sono leggeri e pratici. Quelli spessi (4-6cm) sono robusti e scenografici. L'artigiano ti consiglierà in base all'uso previsto."
  }
];

export default function TaglieriRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    wood_type: '',
    board_type: '',
    size: '',
    finish: '',
    personalization: '',
    purpose: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Taglieri Su Misura | Falegnameria Artigianale | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `board-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi il tagliere che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_board_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const woodLabel = WOOD_TYPES.find(t => t.value === formData.wood_type)?.label || '';
      const boardLabel = BOARD_TYPES.find(t => t.value === formData.board_type)?.label || '';
      const sizeLabel = SIZE_OPTIONS.find(t => t.value === formData.size)?.label || '';
      const finishLabel = FINISH_OPTIONS.find(t => t.value === formData.finish)?.label || '';
      const personalizationLabel = PERSONALIZATIONS.find(t => t.value === formData.personalization)?.label || '';
      const purposeLabel = PURPOSE_OPTIONS.find(p => p.value === formData.purpose)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      let fullDescription = formData.description;
      
      const details = [];
      if (boardLabel) details.push(`🪵  Tipo: ${boardLabel}`);
      if (woodLabel) details.push(`🌳  Legno: ${woodLabel}`);
      if (sizeLabel) details.push(`📐  Dimensioni: ${sizeLabel}`);
      if (finishLabel) details.push(`✨  Finitura: ${finishLabel}`);
      if (personalizationLabel) details.push(`🎨  Personalizzazione: ${personalizationLabel}`);
      if (purposeLabel) details.push(`🎯  Utilizzo: ${purposeLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${boardLabel} in ${woodLabel}`.trim() || 'Tagliere su misura',
        description: fullDescription,
        category: 'Taglieri',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dai falegnami.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('board-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Taglieri Su Misura | Falegnameria Artigianale Italiana | Rarifex</title>
        <meta name="description" content="Taglieri in legno su misura realizzati da maestri falegnami italiani. Ulivo, noce, acacia con incisioni personalizzate. Artigianato Made in Italy. Gratuito e senza impegno." />
        <meta name="keywords" content="tagliere su misura, tagliere legno, tagliere ulivo, tagliere personalizzato, tagliere artigianale, tagliere inciso, regalo tagliere, falegnameria" />
        <link rel="canonical" href="https://www.rarifex.com/taglieri-su-misura" />
        <meta property="og:title" content="Taglieri Su Misura | Falegnameria Artigianale Italiana | Rarifex" />
        <meta property="og:description" content="Taglieri in legno su misura realizzati da maestri falegnami italiani. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/taglieri-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-amber-50/50 via-white to-orange-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Slice className="w-4 h-4" />
                    Falegnameria Su Misura
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Il tagliere perfetto{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">
                      creato per te
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Maestri falegnami italiani creano il tagliere dei tuoi sogni.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Legni pregiati, incisioni personalizzate, pezzi unici.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Artigiani verificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-slate-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="board-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del tuo progetto
                        </Label>
                        <Input
                          placeholder="Es: Tagliere regalo matrimonio"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="board-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi il tagliere che desideri *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... Tipo di legno, dimensioni, incisioni, uso previsto. Allega foto di riferimento se ne hai."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="board-description-textarea"
                        />
                      </div>
                      
                      {/* Board Type & Wood Type */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tipo</Label>
                          <select
                            value={formData.board_type}
                            onChange={(e) => handleInputChange('board_type', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="board-type-select"
                          >
                            <option value="">Seleziona tipo...</option>
                            {BOARD_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Legno</Label>
                          <select
                            value={formData.wood_type}
                            onChange={(e) => handleInputChange('wood_type', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="board-wood-select"
                          >
                            <option value="">Seleziona legno...</option>
                            {WOOD_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Size & Finish */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Dimensioni</Label>
                          <select
                            value={formData.size}
                            onChange={(e) => handleInputChange('size', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="board-size-select"
                          >
                            <option value="">Seleziona...</option>
                            {SIZE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Finitura</Label>
                          <select
                            value={formData.finish}
                            onChange={(e) => handleInputChange('finish', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="board-finish-select"
                          >
                            <option value="">Seleziona...</option>
                            {FINISH_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Personalization & Purpose */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Personalizzazione</Label>
                          <select
                            value={formData.personalization}
                            onChange={(e) => handleInputChange('personalization', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="board-personalization-select"
                          >
                            <option value="">Seleziona...</option>
                            {PERSONALIZATIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Utilizzo</Label>
                          <select
                            value={formData.purpose}
                            onChange={(e) => handleInputChange('purpose', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="board-purpose-select"
                          >
                            <option value="">Seleziona...</option>
                            {PURPOSE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Budget & Timeline */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="board-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tempi</Label>
                          <select
                            value={formData.timeline}
                            onChange={(e) => handleInputChange('timeline', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="board-timeline-select"
                          >
                            <option value="">Seleziona...</option>
                            {TIMELINE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* File Upload */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Immagini di riferimento (opzionale)
                        </Label>
                        <div className="mt-1.5">
                          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-colors">
                            <Upload className="w-5 h-5 text-slate-400" />
                            <span className="text-sm text-slate-500">
                              {uploadingFile ? 'Caricamento...' : 'Carica immagini (max 5)'}
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleFileUpload}
                              className="hidden"
                              disabled={uploadingFile}
                            />
                          </label>
                        </div>
                        
                        {formData.attachments.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {formData.attachments.map((url, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={url}
                                  alt={`Riferimento ${index + 1}`}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeAttachment(index)}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white py-6 text-lg font-semibold shadow-lg"
                        data-testid="board-submit-button"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Invio in corso...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5 mr-2" />
                            Richiedi Preventivi Gratuiti
                          </>
                        )}
                      </Button>
                      
                      <p className="text-xs text-center text-slate-500">
                        Riceverai proposte da falegnami verificati entro 24-48 ore
                      </p>
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Come Funziona
              </h2>
              <p className="text-lg text-slate-600">
                In 4 semplici passi avrai il tuo tagliere artigianale
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {[
                { icon: MessageSquare, title: 'Descrivi', desc: 'Raccontaci il tagliere che desideri' },
                { icon: Users, title: 'Ricevi', desc: 'Proposte da falegnami esperti' },
                { icon: Star, title: 'Scegli', desc: 'L\'artigiano e il progetto ideale' },
                { icon: Heart, title: 'Ammira', desc: 'Il tuo tagliere unico prende vita' },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section className="py-16 bg-gradient-to-b from-amber-50/30 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Taglieri Realizzati
              </h2>
              <p className="text-lg text-slate-600">
                Dalla richiesta al tagliere finito
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {BOARD_EXAMPLES.map((example, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <img
                    src={example.image}
                    alt={example.result}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-5">
                    <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full mb-3">
                      {example.category}
                    </span>
                    <p className="text-sm text-slate-600 mb-2">
                      <strong>Richiesta:</strong> {example.idea}
                    </p>
                    <p className="text-sm text-slate-800">
                      <strong>Risultato:</strong> {example.result}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa Dicono i Clienti
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {TESTIMONIALS.map((testimonial, index) => (
                <Card key={index} className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-4 italic">"{testimonial.text}"</p>
                  <div className="text-sm">
                    <p className="font-semibold text-slate-900">{testimonial.author}</p>
                    <p className="text-slate-500">{testimonial.location}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gradient-to-b from-white to-amber-50/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">
                Domande Frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, index) => (
                  <Card 
                    key={index}
                    className="overflow-hidden cursor-pointer"
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <div className="p-5 flex justify-between items-center">
                      <h3 className="font-semibold text-slate-900">{faq.question}</h3>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {expandedFaq === index && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 bg-gradient-to-r from-amber-600 to-orange-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Pronto a creare il tuo tagliere?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Richiedi preventivi gratuiti dai migliori falegnami italiani
            </p>
            <Button
              onClick={scrollToForm}
              size="lg"
              className="bg-white text-amber-600 hover:bg-amber-50 font-semibold shadow-xl px-8 py-6 text-lg"
              data-testid="board-final-cta"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Inizia Ora - È Gratis
            </Button>
          </div>
        </section>
        
        {/* Auth Prompt Modal */}
        {showAuthPrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Accedi per continuare
              </h3>
              <p className="text-slate-600 mb-6">
                Per inviare la tua richiesta e ricevere proposte dai falegnami, accedi o crea un account.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAuthPrompt(false)}
                >
                  Annulla
                </Button>
                <Button
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={() => navigate('/login?redirect=/taglieri-su-misura')}
                >
                  Accedi
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
